/**
 * VerificationCodeInput - 6-digit code input with auto-focus
 * Single Responsibility: Only handles code input UI and validation
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';

const CODE_LENGTH = 6;

export function VerificationCodeInput({ 
  onComplete, 
  onError,
  isLoading = false,
  error = null,
  autoFocus = true 
}) {
  const [code, setCode] = useState(Array(CODE_LENGTH).fill(''));
  const inputRefs = useRef([]);
  
  // Reset code when error changes
  useEffect(() => {
    if (error) {
      setCode(Array(CODE_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    }
  }, [error]);
  
  // Auto-focus first input
  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);
  
  // Check if code is complete
  const checkComplete = useCallback((newCode) => {
    const fullCode = newCode.join('');
    if (fullCode.length === CODE_LENGTH && newCode.every(digit => digit !== '')) {
      onComplete?.(fullCode);
    }
  }, [onComplete]);
  
  const handleChange = (index, value) => {
    // Only accept digits
    const digit = value.replace(/\D/g, '').slice(-1);
    
    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);
    
    // Move to next input if digit entered
    if (digit && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
    
    // Check if code is complete
    checkComplete(newCode);
  };
  
  const handleKeyDown = (index, e) => {
    // Move to previous input on backspace if current is empty
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    
    // Handle arrow keys
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };
  
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    
    if (pastedData) {
      const newCode = [...code];
      pastedData.split('').forEach((digit, i) => {
        if (i < CODE_LENGTH) {
          newCode[i] = digit;
        }
      });
      setCode(newCode);
      
      // Focus last filled input or next empty
      const lastIndex = Math.min(pastedData.length, CODE_LENGTH) - 1;
      if (lastIndex < CODE_LENGTH - 1) {
        inputRefs.current[lastIndex + 1]?.focus();
      } else {
        inputRefs.current[lastIndex]?.focus();
      }
      
      // Check if complete
      checkComplete(newCode);
    }
  };
  
  const handleFocus = (e) => {
    e.target.select();
  };
  
  return (
    <div className="space-y-4">
      <div className="flex justify-center gap-2 sm:gap-3">
        {code.map((digit, index) => (
          <motion.input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={handleFocus}
            disabled={isLoading}
            className={`
              w-11 h-14 sm:w-12 sm:h-16 
              text-center text-2xl font-bold 
              border-2 rounded-xl
              transition-all duration-200
              focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500
              disabled:opacity-50 disabled:cursor-not-allowed
              ${error 
                ? 'border-red-500 bg-red-50 dark:bg-red-950/20 text-red-600' 
                : digit 
                  ? 'border-orange-500 bg-orange-50 dark:bg-orange-950/20 text-orange-600' 
                  : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white'
              }
            `}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: index * 0.05 }}
            data-testid={`verification-code-input-${index}`}
          />
        ))}
      </div>
      
      {/* Loading indicator */}
      {isLoading && (
        <motion.div 
          className="flex justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
        </motion.div>
      )}
      
      {/* Error message */}
      {error && (
        <motion.p 
          className="text-center text-sm text-red-600 dark:text-red-400"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="verification-error"
        >
          {error}
        </motion.p>
      )}
    </div>
  );
}

export default VerificationCodeInput;
